/**
 * Foundry → Agents (D441): the catalogue of Foundry agents people publish for the organisation. Each is versioned and
 * immutable; a version the organisation reviews waits for a reviewer, who approves it or rejects it with a reason. A
 * withdrawn agent stays readable. Pressing an agent's name opens its versions.
 */

import type { FoundryAgentListing, FoundryAgentVersion } from '@aioe/shared';
import { CircleQuestion, Cloud, Persons, SealCheck } from '@gravity-ui/icons';
import { useState } from 'react';

import { foundryRoutes, problemText } from '../../api/client.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { Button, ConfirmDialog, Empty, ErrorNote, inputClass, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark } from '../ui.tsx';
import { FormField, FormSheet } from '../ui/FormSheet.tsx';
import { RowMenu } from '../ui/RowMenu.tsx';
import { AGENT_STATE_MARK, AGENT_STATE_WORDS, hostOf, plural, ROLE_WORDS, usePersonName, type AgentState } from './foundryWords.ts';
import { TONE } from './tone.ts';

const stateOf = (l: FoundryAgentListing): AgentState => (l.withdrawnAt ? 'withdrawn' : l.latest.state);

/** " by Jo Citizen" when the directory knows who, nothing when it does not. */
function PublishedBy({ id }: { id: string }) {
  const name = usePersonName(id);
  return name ? <> by {name}</> : null;
}

function AgentDescription({ l }: { l: FoundryAgentListing }) {
  const d = l.latest.definition;
  return (
    <>
      {l.description ? <span className="block">{l.description}</span> : null}
      <span className="block">
        Version {l.latest.version} · offered {ROLE_WORDS[l.latest.as]} · {d.deployment} on {hostOf(d.projectEndpoint)}
      </span>
      <span className="block tabular-nums">
        Published<PublishedBy id={l.latest.publishedBy} /> {timeAgo(l.latest.publishedAt)} · {l.usedBy === 0 ? 'nobody uses it yet' : `${plural(l.usedBy, 'person', 'people')} use it`}
      </span>
    </>
  );
}

export function AgentsPanel({ mayReview }: { mayReview: boolean }) {
  const list = useLoad(() => foundryRoutes.agents(), [], 60_000);
  const [opened, setOpened] = useState<FoundryAgentListing>();
  const [rejecting, setRejecting] = useState<FoundryAgentListing>();
  const [withdrawing, setWithdrawing] = useState<FoundryAgentListing>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const agents = list.data?.agents;
  const canReview = mayReview && (list.data?.canReview ?? false);
  const held = (agents ?? []).filter((l) => !l.withdrawnAt && l.latest.state === 'held');
  const rest = (agents ?? []).filter((l) => !held.includes(l));
  const listed = (agents ?? []).filter((l) => !l.withdrawnAt && l.latest.state === 'listed');
  const people = (agents ?? []).reduce((n, l) => n + l.usedBy, 0);

  const approve = async (l: FoundryAgentListing) => {
    setBusy(l.agentId);
    setError(undefined);
    try {
      await foundryRoutes.reviewAgent(l.agentId, l.latest.version, { decision: 'approve' });
      list.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const menu = (l: FoundryAgentListing) => (
    <RowMenu
      label={`More for ${l.name}`}
      items={[{ id: 'versions', label: 'Versions…' }, !l.withdrawnAt && canReview && { id: 'withdraw', label: 'Withdraw…', danger: true }]}
      onAction={(id) => (id === 'versions' ? setOpened(l) : setWithdrawing(l))}
    />
  );
  const title = (l: FoundryAgentListing) => (
    <button type="button" className="text-left font-medium hover:underline" onClick={() => setOpened(l)}>
      {l.name}
    </button>
  );

  return (
    <PageColumn width="data">
      <PageHeader tone={TONE} icon={<SealCheck />} title="Foundry agents" subtitle="The agents people publish from their Foundry projects for the organisation, and what waits for review." />
      <ErrorNote error={list.error ?? error} />
      <div className="art-stats">
        <Stat tone={TONE} icon={<Cloud />} label="Listed" value={agents ? listed.length : undefined} detail={agents ? 'Offered to everyone' : ''} />
        <Stat tone={TONE} icon={<CircleQuestion />} label="Waiting for review" value={agents ? held.length : undefined} detail={held.length ? `Oldest ${timeAgo(held.map((l) => l.latest.publishedAt).sort()[0]!)}` : 'Nothing waiting'} needsYou={held.length > 0} />
        <Stat tone={TONE} icon={<Persons />} label="In use" value={agents ? people : undefined} detail="Times people added an agent" />
      </div>

      {held.length ? (
        <ListGroup title="Waiting for review" count={held.length} description="Approving lists the version for everyone. Rejecting tells the publisher why.">
          {held.map((l) => (
            <ListRow
              key={l.agentId}
              icon={<Cloud />}
              title={title(l)}
              description={<AgentDescription l={l} />}
              control={
                canReview ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="primary" isPending={busy === l.agentId} onPress={() => void approve(l)}>
                      Approve
                    </Button>
                    <Button size="sm" variant="secondary" isDisabled={busy === l.agentId} onPress={() => setRejecting(l)}>
                      Reject
                    </Button>
                    {menu(l)}
                  </div>
                ) : (
                  menu(l)
                )
              }
            />
          ))}
        </ListGroup>
      ) : null}

      <ListGroup
        title="Catalogue"
        count={agents ? rest.length : undefined}
        description="Each agent shows its newest version. People add one as a specialist or as a member of a team."
        empty={agents && rest.length === 0 ? <Empty tone={TONE} icon={<SealCheck />} title="No agents published yet" body="When someone publishes an agent from a Foundry project the organisation has approved, it is listed here with its versions and how many people use it." /> : undefined}
      >
        {!agents && !list.error ? <ListRow title="Reading the catalogue…" /> : null}
        {rest.map((l) => (
          <ListRow key={l.agentId} icon={<Cloud />} title={title(l)} description={<AgentDescription l={l} />} status={<StatusMark state={AGENT_STATE_MARK[stateOf(l)]} label={AGENT_STATE_WORDS[stateOf(l)]} />} compactControl control={menu(l)} />
        ))}
      </ListGroup>

      {opened ? <VersionsSheet listing={opened} onClose={() => setOpened(undefined)} /> : null}
      {rejecting ? (
        <RejectSheet
          listing={rejecting}
          onClose={() => setRejecting(undefined)}
          onDone={() => {
            setRejecting(undefined);
            list.reload();
          }}
        />
      ) : null}
      <ConfirmDialog
        isOpen={Boolean(withdrawing)}
        title={`Withdraw ${withdrawing?.name ?? ''}?`}
        confirmLabel="Withdraw"
        danger
        onCancel={() => setWithdrawing(undefined)}
        onConfirm={async () => {
          const l = withdrawing!;
          setWithdrawing(undefined);
          setError(undefined);
          try {
            await foundryRoutes.withdrawAgent(l.agentId);
            list.reload();
          } catch (e) {
            setError(problemText(e));
          }
        }}
      >
        Every version stops being offered, and a workflow cannot name it any more. The agent stays here, readable, as withdrawn.
      </ConfirmDialog>
    </PageColumn>
  );
}

/** A reason is required to reject, so the publisher knows what to change. */
function RejectSheet({ listing, onClose, onDone }: { listing: FoundryAgentListing; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <FormSheet
      width="md"
      title={`Reject ${listing.name}`}
      description={`Version ${listing.latest.version} is not listed. The publisher reads your reason.`}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger-soft"
            isPending={busy}
            isDisabled={!note.trim()}
            onPress={() => {
              setBusy(true);
              setError(undefined);
              foundryRoutes.reviewAgent(listing.agentId, listing.latest.version, { decision: 'reject', note: note.trim() }).then(onDone, (e) => {
                setError(problemText(e));
                setBusy(false);
              });
            }}
          >
            Reject
          </Button>
        </>
      }
    >
      <FormField label="Why">
        <textarea className={`${inputClass} resize-y`} rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </FormField>
      <ErrorNote error={error} />
    </FormSheet>
  );
}

/** One agent's versions, newest first: what each stands on, who published it and how it was decided. */
function VersionsSheet({ listing, onClose }: { listing: FoundryAgentListing; onClose: () => void }) {
  const detail = useLoad(() => foundryRoutes.agent(listing.agentId), [listing.agentId]);
  const versions: FoundryAgentVersion[] = detail.data?.versions ?? [];
  return (
    <FormSheet width="lg" title={listing.name} description={listing.description ?? 'Every version is kept as it was published.'} onClose={onClose} footer={<Button variant="tertiary" onPress={onClose}>Close</Button>}>
      <ErrorNote error={detail.error} />
      {!detail.data && !detail.error ? <p className="text-sm text-muted">Reading the versions…</p> : null}
      <div className="grid gap-3">
        {versions.map((v) => {
          const state: AgentState = listing.withdrawnAt ? 'withdrawn' : v.state;
          return (
            <div key={v.version} className="grid gap-1 border-b border-separator pb-3 last:border-b-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium">Version {v.version}</span>
                <StatusMark state={AGENT_STATE_MARK[state]} label={AGENT_STATE_WORDS[state]} />
              </div>
              <span className="text-xs text-muted">
                Offered {ROLE_WORDS[v.as]} · {v.definition.deployment} on {hostOf(v.definition.projectEndpoint)}
              </span>
              <span className="text-xs text-muted tabular-nums">
                Published<PublishedBy id={v.publishedBy} /> {timeAgo(v.publishedAt)}
                {v.decidedAt ? ` · decided ${timeAgo(v.decidedAt)}` : ''}
              </span>
              {v.definition.hostedTools.length ? <span className="text-xs text-muted">Foundry tools: {v.definition.hostedTools.join(', ')}</span> : null}
              {v.notes ? <span className="text-xs">“{v.notes}”</span> : null}
              {v.note ? <span className="text-xs">Reviewer’s note: {v.note}</span> : null}
            </div>
          );
        })}
      </div>
    </FormSheet>
  );
}
